import React from 'react';
import { Column } from 'react-table';

import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  MappingCell,
  YesNoCell,
  CellFunction,
  NAGuard,
  NoneGuard
} from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useTerms } from '../../../../../contexts/terms';
import { IEpxHeart, Variant } from '../../../../../libs/apps/epxheart';
import { TriStateLiteral } from '../../../../../libs/session';
import moment from 'moment';

interface HeaderProps {
  title: string;
  content: string;
}

const HeaderWithTooltip = ({ title, content }: HeaderProps) => {
  return (
    <Tooltip content={content}>
      <span>
        {title}
        <Icon className="ml-2" icon={faInfoCircle} />
      </span>
    </Tooltip>
  );
};

export const BloodPressureCell: CellFunction<IEpxHeart> = ({
  row: { original: session }
}) => {
  const upper = session.bpUpper as number;
  const lower = session.bpLower as number;
  return (
    NoneGuard(upper, lower) || (
      <span>
        {upper || '?'}/{lower || '?'}
      </span>
    )
  );
};

const Weight: CellFunction<IEpxHeart> = ({ row: { original: session } }) => {
  const weight = session?.weight?.toFixed(2);
  const invalidated = session?.invalidation?.key === 'weight';
  const weightNewValue = session?.invalidation?.newValue;
  const invalidationDate = session?.invalidation?.createdAt
    ? moment(session.invalidation.createdAt).format('MMM D, YYYY h:mm A')
    : null;
  const prevValue = session?.invalidation?.previousValue;
  if (invalidated) {
    const tooltipText = `This session was invalidated on ${
      invalidationDate || 'unknown date'
    } and the Original value was ${prevValue ?? '?'}`;
    return (
      <Tooltip content={tooltipText} position="top">
        <span>{weightNewValue != null ? weightNewValue : '?'}*</span>
      </Tooltip>
    );
  } else if (weight != null) {
    return <span>{weight}</span>;
  }
  return NoneGuard(weight);
};

const KCCQCell: CellFunction<IEpxHeart> = ({ row: { original: session } }) => {
  const kPhysical = session?.kPhysical;
  const isPAHindered = session?.isPAHindered;
  const isBinary = session?.isBinary;
  let value: boolean | number | string = null;
  if (isBinary) {
    if (isPAHindered != null) {
      value =
        isPAHindered === true
          ? 'Physical Activity is Hindered'
          : 'Physical Activity not Hindered';
    }
  } else if (kPhysical != null) {
    value = kPhysical;
  }
  if (value != null) {
    return <span>{value}</span>;
  }
  return NoneGuard(value);
};

const GetNACheckVariant = (variant?: string) => {
  return (session: IEpxHeart): boolean => {
    if (variant) {
      return session.variant !== variant;
    }
    return session.variant == null;
  };
};

const GetNACheckNonVariant = () => {
  return (session: IEpxHeart): boolean => {
    return session.variant !== null;
  };
};

const GetNACheckSession = (flag: 'isWeight' | 'isKCCQ') => {
  return (session: IEpxHeart): boolean => {
    const isNA = session[flag] == null || session[flag] === false;
    const isBP =
      session.isBP || session.bpLower != null || session.bpUpper != null;
    if (flag === 'isKCCQ') {
      return isNA || isBP;
    }
    return isNA;
  };
};

type Dependencies = [Variant, boolean, boolean, boolean, boolean];

const getDependencies = (sessions: IEpxHeart[]): Dependencies => {
  let variant: Variant = null;
  let hasBP = false;
  let hasSOB = false;
  let hasBinary = false;
  let hasKCCQData = false;
  let hasKCCQEnabledInSchedule = false;
  for (const session of sessions) {
    if (session.variant === Variant.A) {
      if (variant !== Variant.B) {
        variant = Variant.A;
      }
    } else if (session.variant === Variant.B) {
      variant = Variant.B;
    } else if (session.variant === Variant.C) {
      variant = Variant.C;
    }
    if (session.isBP) {
      hasBP = true;
    }
    if (session.isSOBSymptoms) {
      hasSOB = true;
    }
    if (session.isBinary) {
      hasBinary = true;
    }
    if (
      session.isKCCQ === true ||
      session.kPhysical != null ||
      session.isPAHindered != null
    ) {
      hasKCCQData = true;
    }
    const scheduleKCCQ = session.schedule?.shouldUseKCCQ;
    if (scheduleKCCQ === true) {
      hasKCCQEnabledInSchedule = true;
    }
  }
  const shouldShowKCCQ = hasKCCQData || hasKCCQEnabledInSchedule;
  return [variant, hasBP, hasSOB, hasBinary, shouldShowKCCQ];
};

const getColumns = (sessions: IEpxHeart[]): Column<IEpxHeart>[] => {
  const { terms } = useTerms();
  const [variant, hasBP, hasSOB, hasBinary, shouldShowKCCQ] =
    getDependencies(sessions);
  const isSameSession = (session: IEpxHeart) => {
    return session.variant === sessions[0].variant;
  };
  const allSame: boolean = sessions.every(isSameSession);

  // Compose columns based on variant
  let columns: Column<IEpxHeart>[] = [];

  // Return N/A for dpe and orhtopnea columns if variant session
  // Nonvariant sessions have dyspnea/edema as one column
  // Using loose equality because variant could be undefined or null
  if (allSame === true && sessions[0]?.variant == null) {
    columns = columns.concat([
      {
        Header: (
          <HeaderWithTooltip
            title="Dyspnea/Pedal Edema"
            content={`This column will only be present if the ${terms.patient.default} is enrolled in a non-variant version of the program.`}
          />
        ),
        accessor: 'dpe',
        Cell: ResponseGuard(
          MappingCell('dpe', TriStateLiteral) as CellFunction<IEpxHeart>
        )
      },
      {
        Header: (
          <HeaderWithTooltip
            title="Orthopnea"
            content={`This column will only be present if the ${terms.patient.default} is enrolled in a non-variant version of the program.`}
          />
        ),
        accessor: 'orthopnea',
        Cell: ResponseGuard(
          MappingCell('orthopnea', TriStateLiteral) as CellFunction<IEpxHeart>
        )
      },
      {
        Header: (
          <HeaderWithTooltip
            title="Excessive Weight Gain"
            content={`There will only be a value in this column if the ${terms.patient.default} was asked about their weight. Othwerwise, it will be N/A.`}
          />
        ),
        accessor: 'hasExcessiveWeightGain',
        Cell: NAGuard(
          ResponseGuard(
            YesNoCell('hasExcessiveWeightGain') as CellFunction<IEpxHeart>
          ),
          GetNACheckSession('isWeight')
        )
      }
    ]);
  }

  // If Variant A, B or C, then add the separate Dyspnea and Edema columns.
  // Return N/A if not a variant session.
  if (allSame === true && ['A', 'B', 'C'].includes(variant)) {
    columns = columns.concat([
      {
        Header: 'Dyspnea',
        accessor: 'dyspnea',
        Cell: ResponseGuard(
          MappingCell('dyspnea', TriStateLiteral) as CellFunction<IEpxHeart>
        )
      },
      {
        Header: 'Edema',
        accessor: 'edema',
        Cell: ResponseGuard(
          MappingCell('edema', TriStateLiteral) as CellFunction<IEpxHeart>
        )
      },
      {
        Header: (
          <HeaderWithTooltip
            title="Excessive Weight Gain"
            content={`There will only be a value in this column if the ${terms.patient.default} was asked about their weight. Othwerwise, it will be N/A.`}
          />
        ),
        accessor: 'hasExcessiveWeightGain',
        Cell: NAGuard(
          ResponseGuard(
            YesNoCell('hasExcessiveWeightGain') as CellFunction<IEpxHeart>
          ),
          GetNACheckSession('isWeight')
        )
      }
    ]);
  }

  if (allSame === false) {
    columns = columns.concat([
      {
        Header: 'Dyspnea',
        accessor: 'dyspnea',
        Cell: NAGuard(
          ResponseGuard(
            MappingCell('dyspnea', TriStateLiteral) as CellFunction<IEpxHeart>
          ),
          GetNACheckVariant()
        )
      },
      {
        Header: 'Edema',
        accessor: 'edema',
        Cell: NAGuard(
          ResponseGuard(
            MappingCell('edema', TriStateLiteral) as CellFunction<IEpxHeart>
          ),
          GetNACheckVariant()
        )
      },
      {
        Header: (
          <HeaderWithTooltip
            title="Excessive Weight Gain"
            content={`There will only be a value in this column if the ${terms.patient.default} was asked about their weight. Othwerwise, it will be N/A.`}
          />
        ),
        accessor: 'hasExcessiveWeightGain',
        Cell: NAGuard(
          ResponseGuard(
            YesNoCell('hasExcessiveWeightGain') as CellFunction<IEpxHeart>
          ),
          GetNACheckSession('isWeight')
        )
      },
      {
        Header: (
          <HeaderWithTooltip
            title="Dyspnea/Pedal Edema"
            content={`You're seeing extra columns because the ${terms.patient.default} received messages from different variants of this program.`}
          />
        ),
        accessor: 'dpe',
        Cell: NAGuard(
          ResponseGuard(
            MappingCell('dpe', TriStateLiteral) as CellFunction<IEpxHeart>
          ),
          GetNACheckNonVariant()
        )
      },
      {
        Header: (
          <HeaderWithTooltip
            title="Orthopnea"
            content={`You're seeing extra columns because the ${terms.patient.default} received messages from different variants of this program.`}
          />
        ),
        accessor: 'orthopnea',
        Cell: NAGuard(
          ResponseGuard(
            MappingCell('orthopnea', TriStateLiteral) as CellFunction<IEpxHeart>
          ),
          GetNACheckNonVariant()
        )
      }
    ]);
  }

  // Always add the Weight column regardless of variant
  columns = columns.concat([
    {
      Header: (
        <HeaderWithTooltip
          title="Weight (lbs)"
          content={`There will only be a value in this column if the ${terms.patient.default}'s prescription has "Track Exact Weight" turned on.`}
        />
      ),
      accessor: 'weight',
      Cell: NAGuard(ResponseGuard(Weight), GetNACheckSession('isWeight'))
    }
  ]);
  if (shouldShowKCCQ) {
    columns.push({
      Header: (
        <HeaderWithTooltip
          title="KCCQ Physical"
          content={`The value in this column will be a number from 0 (never) to 5 (all the time) if the ${terms.patient.default} does not have the binary mode turned on. Otherwise, it will the response to a yes/no question. It will be N/A if the KCCQ question was not asked at the time, or if "Ask about Blood Pressure" in the ${terms.patient.default}'s prescription is turned on.`}
        />
      ),
      accessor: hasBinary ? 'isPAHindered' : 'kPhysical',
      Cell: NAGuard(KCCQCell, GetNACheckSession('isKCCQ'))
    });
  }
  // If Variant B then append the Outreach column
  if (['B'].includes(variant)) {
    columns.push({
      Header: (
        <HeaderWithTooltip
          title="Wants Outreach"
          content={`There will only be a value in this column if the ${terms.patient.default} is enrolled in variant B of this program. Otherwise, it will show up as N/A or the column will not be present.`}
        />
      ),
      accessor: 'wantsToSpeak',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('wantsToSpeak') as CellFunction<IEpxHeart>),
        GetNACheckVariant('B')
      )
    });
  }
  // If using BP then add the BP column
  if (hasBP) {
    columns.push({
      Header: 'Blood Pressure',
      id: 'bp',
      Cell: ResponseGuard(BloodPressureCell as CellFunction<IEpxHeart>)
    });
  }
  // If using SOB then add the SOB columns
  if (hasSOB) {
    columns.push({
      Header: (
        <Tooltip content="Awakened by Shortness of Breath">
          Awakened by S.O.B.
        </Tooltip>
      ),
      id: 'hasSOBAwakened',
      Cell: ResponseGuard(
        YesNoCell('hasSOBAwakened') as CellFunction<IEpxHeart>
      )
    });
    columns.push({
      Header: (
        <Tooltip content="Shortness of Breath while Lying Down">
          S.O.B. Lying Down
        </Tooltip>
      ),
      id: 'lyingDownSOB',
      Cell: ResponseGuard(
        MappingCell('lyingDownSOB', TriStateLiteral) as CellFunction<IEpxHeart>
      )
    });
  }
  return columns;
};

const EpxHeartTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxHeart>
      url="/epxheart/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      appKey="epxheart"
      invalidationModel="EpxHeart"
      {...props}
    />
  );
};

export default EpxHeartTable;
